import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * Juggle Factor — FTUE demo (3 essais).
 *
 * E1 : 1-back, séquence courte, on isole un match évident (T → T)
 * E2 : 1-back, foil (T → R) pour matérialiser un "pas de match"
 * E3 : 2-back, intro à la charge mnésique réelle
 */
import { useEffect, useRef, useState } from 'react';
const DEMO_STEPS = [
    {
        idx: 1,
        title: 'Essai 1 — 1-back, match évident',
        hint: 'Compare la lettre courante à celle juste avant. Ici T puis T : c\'est un match.',
        sequence: ['T', 'T'],
        nBack: 1,
        expected: 'match',
    },
    {
        idx: 2,
        title: 'Essai 2 — 1-back, pas de match',
        hint: 'T puis R : différentes. Tape "Différent".',
        sequence: ['T', 'R'],
        nBack: 1,
        expected: 'no-match',
    },
    {
        idx: 3,
        title: 'Essai 3 — 2-back',
        hint: 'Désormais compare à 2 positions plus tôt. M, K, M : la courante M correspond à la M d\'il y a 2 positions.',
        sequence: ['M', 'K', 'M'],
        nBack: 2,
        expected: 'match',
    },
];
export function Demo({ onComplete, onSkip }) {
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('reveal');
    const [revealed, setRevealed] = useState(0);
    const [picked, setPicked] = useState(null);
    const revealTimer = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    useEffect(() => {
        setRevealed(0);
        setPicked(null);
        setPhase('reveal');
        let i = 0;
        const tick = () => {
            i += 1;
            setRevealed(i);
            if (i >= step.sequence.length) {
                setPhase('awaiting');
                return;
            }
            revealTimer.current = setTimeout(tick, 700);
        };
        revealTimer.current = setTimeout(tick, 600);
        return () => {
            if (revealTimer.current)
                clearTimeout(revealTimer.current);
        };
    }, [stepIdx, step.sequence.length]);
    const correct = (() => {
        if (phase !== 'feedback')
            return null;
        return picked === step.expected;
    })();
    const handlePick = (a) => {
        if (phase !== 'awaiting')
            return;
        setPicked(a);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
    };
    const visible = step.sequence.slice(0, revealed);
    const current = visible[visible.length - 1];
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsxs("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: ["D\u00E9mo guid\u00E9e \u2014 n-back = ", step.nBack] }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: step.title }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: step.hint }), _jsxs("div", { className: "relative flex h-[200px] w-full max-w-[320px] items-center justify-center", style: { background: '#1a1a1a' }, children: [phase === 'reveal' && current && (_jsx("span", { className: "font-display text-[96px] text-cyan-surface", children: current })), (phase === 'awaiting' || phase === 'feedback') && current && (_jsx("span", { className: "font-display text-[96px] text-cyan-surface", children: current })), phase === 'feedback' && (_jsx("span", { className: [
                            'absolute inset-0 flex items-center justify-center font-display text-[28px]',
                            correct ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), style: { background: 'rgba(0,0,0,0.7)' }, children: correct ? '✓' : '✗' }))] }), _jsx("div", { className: "flex gap-s2 text-[11px] text-cyan-text-secondary", children: step.sequence.map((l, i) => (_jsx("span", { className: [
                        'flex h-[28px] w-[28px] items-center justify-center rounded-full border',
                        i < revealed ? 'border-cyan-core text-cyan-text' : 'border-cyan-border-faint text-cyan-text-faint',
                    ].join(' '), children: i < revealed ? l : '·' }, i))) }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'awaiting' && (_jsxs("div", { className: "flex justify-center gap-s3", children: [_jsx("button", { type: "button", onClick: () => handlePick('no-match'), "aria-label": "Pas de match", className: "flex h-[56px] min-w-[110px] items-center justify-center border border-cyan-border bg-cyan-surface px-s4 text-[14px] text-cyan-text hover:scale-[1.03] hover:bg-cyan-bg active:scale-95", children: "Diff\u00E9rent" }), _jsx("button", { type: "button", onClick: () => handlePick('match'), "aria-label": "Match", className: "flex h-[56px] min-w-[110px] items-center justify-center bg-cyan-core px-s4 text-[14px] text-cyan-surface hover:scale-[1.03] hover:bg-cyan-network active:scale-95", children: "Match" })] })), phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? 'Démarrer la session' : 'Essai suivant' })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: "Passer la d\u00E9mo" })] })] }));
}
//# sourceMappingURL=demo.js.map